import { Card, CardContent, Typography, Box } from "@mui/material";
import { Gauge, gaugeClasses } from "@mui/x-charts/Gauge";

export interface RedemptionGaugeProps {
  rate: number; // Redemption rate percentage (0-100)
}

export function RedemptionGauge({ rate }: RedemptionGaugeProps) {
  // Determine color based on rate
  const getColor = () => {
    if (rate < 10) return "#f44336"; // Red (Critical - poor engagement)
    if (rate < 20) return "#ff9800"; // Orange (Warning - low engagement)
    if (rate <= 40) return "#4caf50"; // Green (Healthy)
    if (rate <= 60) return "#ff9800"; // Orange (Warning - moderate risk)
    return "#f44336"; // Red (Critical - high liability risk)
  };

  const getStatusText = () => {
    if (rate < 10) return "Very Low - Needs Incentive";
    if (rate < 20) return "Low - Monitor";
    if (rate <= 40) return "Healthy - Good Rate";
    if (rate <= 60) return "High - Monitor";
    return "Very High - Financial Risk";
  };

  const getStatusColor = () => {
    if (rate < 10) return "error.main";
    if (rate < 20) return "warning.main";
    if (rate <= 40) return "success.main";
    if (rate <= 60) return "warning.main";
    return "error.main";
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ textAlign: "center" }}>
          Redemption Rate
        </Typography>

        <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
          <Gauge
            value={rate}
            startAngle={-110}
            endAngle={110}
            sx={{
              [`& .${gaugeClasses.valueText}`]: {
                fontSize: 32,
                fontWeight: 700,
                fill: getColor(),
              },
              [`& .${gaugeClasses.valueArc}`]: {
                fill: getColor(),
              },
              [`& .${gaugeClasses.referenceArc}`]: {
                fill: "rgba(0, 0, 0, 0.1)",
              },
            }}
            width={200}
            height={200}
            text={({ value }) => `${(value || 0).toFixed(1)}%`}
          />
        </Box>

        {/* Status Text */}
        <Box sx={{ textAlign: "center", mt: 2, px: 2 }}>
          <Typography
            variant="body1"
            sx={{
              color: getStatusColor(),
              fontWeight: 600,
              mb: 1,
            }}
          >
            {getStatusText()}
          </Typography>

          {/* Legend */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mt: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
              <Box sx={{ width: 12, height: 12, bgcolor: "#4caf50", borderRadius: 1 }} />
              <Typography variant="caption" color="text.secondary">
                Healthy: 20-40%
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
              <Box sx={{ width: 12, height: 12, bgcolor: "#ff9800", borderRadius: 1 }} />
              <Typography variant="caption" color="text.secondary">
                Warning: 10-20% or 40-60%
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
              <Box sx={{ width: 12, height: 12, bgcolor: "#f44336", borderRadius: 1 }} />
              <Typography variant="caption" color="text.secondary">
                Critical: &lt;10% or &gt;60%
              </Typography>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
